import {create} from 'zustand';
import {devtools, persist} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import type {TransformedExperimentGroup} from '@/types/modest';

export interface ExperimentOverride {
  groupName: string;
  variantName: string;
  variantId: string;
  variantDisplayName: string;
}

export interface ModestExperimentOverrideState {
  isModalOpen: boolean;
  availableExperiments: TransformedExperimentGroup[];
  // Store overrides per app ID
  overridesByApp: Record<string, ExperimentOverride[]>;
  isEnabled: boolean;
}

const initialState: ModestExperimentOverrideState = {
  isModalOpen: false,
  availableExperiments: [],
  overridesByApp: {},
  isEnabled: false,
};

const modestExperimentOverrideStore = create<ModestExperimentOverrideState>()(
  devtools(
    persist(
      immer(() => initialState),
      {
        name: 'modest-experiment-overrides',
        partialize: (state) => ({
          // Only persist the app-specific overrides, not the UI state
          overridesByApp: state.overridesByApp,
        }),
      }
    )
  )
);

const setIsModalOpen = (isOpen: boolean) =>
  modestExperimentOverrideStore.setState((state) => {
    state.isModalOpen = isOpen;
  });

const setAvailableExperimentGroups = (
  experimentGroups: TransformedExperimentGroup[]
) =>
  modestExperimentOverrideStore.setState((state) => {
    state.availableExperiments = experimentGroups;
  });

const addOverride = (appId: string, override: ExperimentOverride) =>
  modestExperimentOverrideStore.setState((state) => {
    if (!state.overridesByApp[appId]) {
      state.overridesByApp[appId] = [];
    }

    state.overridesByApp[appId] = state.overridesByApp[appId].filter(
      (o) => o.groupName !== override.groupName
    );

    state.overridesByApp[appId].push(override);
  });

const removeOverride = (appId: string, groupName: string) =>
  modestExperimentOverrideStore.setState((state) => {
    if (state.overridesByApp[appId]) {
      state.overridesByApp[appId] = state.overridesByApp[appId].filter(
        (o) => o.groupName !== groupName
      );
    }
  });

const clearAllOverrides = (appId: string) =>
  modestExperimentOverrideStore.setState((state) => {
    if (state.overridesByApp[appId]) {
      state.overridesByApp[appId] = [];
    }
  });

const setIsEnabled = (enabled: boolean) =>
  modestExperimentOverrideStore.setState((state) => {
    state.isEnabled = enabled;
  });

const getOverrideForExperiment = (
  appId: string,
  groupName: string
): ExperimentOverride | null => {
  const state = modestExperimentOverrideStore.getState();
  const appOverrides = state.overridesByApp[appId];
  if (!appOverrides) return null;

  return appOverrides.find((o) => o.groupName === groupName) || null;
};

const getCurrentOverrides = (appId: string): ExperimentOverride[] => {
  const state = modestExperimentOverrideStore.getState();
  return state.overridesByApp[appId] || [];
};

export const modestExperimentOverrideActions = {
  setIsModalOpen,
  setAvailableExperimentGroups,
  addOverride,
  removeOverride,
  clearAllOverrides,
  setIsEnabled,
  getOverrideForExperiment,
  getCurrentOverrides,
};

export default modestExperimentOverrideStore;
